/**
 * useAuth Hook
 *
 * Custom hook for accessing authentication state from AuthContext.
 * Provides type-safe access to user data and auth utilities.
 *
 * @layer features
 * @slice auth
 * @segment model
 * @example
 * function UserMenu() {
 *   const { user, logout, isLoading } = useAuth();
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!user) return null;
 *
 *   return (
 *     <DropdownMenu>
 *       <DropdownMenuTrigger>{user.email}</DropdownMenuTrigger>
 *       <DropdownMenuContent>
 *         <DropdownMenuItem onClick={logout}>Logout</DropdownMenuItem>
 *       </DropdownMenuContent>
 *     </DropdownMenu>
 *   );
 * }
 *
 * @throws Error if used outside of AuthProvider
 * @added DOC-0073 (2025-12-19)
 * @updated DOC-0094 (2025-12-26) - Migrated to FSD features/auth
 */

import { useContext } from 'react';
import { AuthContext } from './auth-context';
import type { SessionContextValue } from '@entities/session';

/**
 * Hook to access authentication context
 *
 * Returns the current authentication state including:
 * - user: Current user object or null
 * - permissions: Serialized CASL rules or null
 * - isAuthenticated: Boolean indicating if user is logged in
 * - isLoading: Boolean indicating if auth state is loading
 * - error: Any error that occurred during authentication
 * - logout: Function to log out the user
 * - refetch: Function to refresh auth state
 *
 * @throws Error if used outside of AuthProvider
 * @returns SessionContextValue with user data and auth utilities
 *
 * @example
 * const { user, isAuthenticated, logout } = useAuth();
 */
export function useAuth(): SessionContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      'useAuth must be used within an AuthProvider. ' +
        'Wrap your app in <AuthProvider> in the root layout.'
    );
  }

  return context;
}

/**
 * Hook to optionally access authentication context
 *
 * Unlike useAuth(), this hook returns null instead of throwing
 * when used outside of AuthProvider. Useful for components that
 * may render both inside and outside auth context.
 *
 * @returns SessionContextValue or null if outside AuthProvider
 *
 * @example
 * const auth = useOptionalAuth();
 * if (auth?.isAuthenticated) {
 *   // Show user-specific content
 * }
 */
export function useOptionalAuth(): SessionContextValue | null {
  return useContext(AuthContext);
}
